import { useState, useEffect, useRef, useCallback } from "react"
import { axios } from "@/contexts/auth"
import Modal from "@/components/modals/Modal"
import { ModalBody, ModalContent, ModalFooter } from "@nextui-org/modal"
import { toast } from "react-toastify"
import { ReactComponent as HeadphoneIcon } from "@/icons/headphone-icon.svg"
import { ReactComponent as ToolIcon } from "@/icons/tool-icon.svg"
import { ReactComponent as ShareIcon } from "@/icons/share-icon.svg"
import { ReactComponent as CloudIcon } from "@/icons/cloud-icon.svg"
import Button from "@/components/Button"
import { Preset } from "@/hooks/presets"
import LoadingSpinner from "@/components/LoadingSpinner"
import { Select, SelectItem } from "@nextui-org/select"
import { useFirmware, FIRMWARE_OUTDATED } from "@/hooks/firmware"
import { FIRMWARE_URL } from "@/constants"
import { useLocation } from "react-router-dom"
import { NavLink } from "react-router-dom"
import { useAuth } from "@/contexts/auth"

type HeadphoneBrand = {
  id: number
  name: string
}

type Headphone = {
  id: number
  brand: HeadphoneBrand
  model: string
}

type Props = {
  isOpen: boolean
  onClose: () => void
  onPresetSelected: (preset: Preset) => void
  createPreset: (preset: {
    name: string
    mode: string
    config: object
    headphone?: {
      id: number
      brand: {
        id: number
        name: string
      }
      model: string
    } | null
  }) => Promise<Preset | false>
}

const SharedPresetsModal = ({ isOpen, onClose, onPresetSelected, createPreset }: Props) => {
  const { isAuthenticated } = useAuth()
  const [presets, setPresets] = useState([])
  const [brands, setBrands] = useState<HeadphoneBrand[]>([])
  const [headphones, setHeadphones] = useState<Headphone[]>([])
  const [selectedBrandId, setSelectedBrandId] = useState<number | null>(null)
  const [selectedHeadphone, setSelectedHeadphone] = useState<Headphone | null>(null)
  const [selectedCreatorId, setSelectedCreatorId] = useState<number | null>(null)
  const [isLoading, setIsLoading] = useState(false)
  const [isSaving, setIsSaving] = useState(false)
  const [nextPage, setNextPage] = useState<string | null>(null)
  const [hasMore, setHasMore] = useState(true)
  const scrollContainerRef = useRef<HTMLDivElement>(null)
  const { firmwareStatus, latestFirmware, deviceFirmwareVersion } = useFirmware()
  const location = useLocation()

  // Check if we're in the process of updating firmware
  const isFirmwareUpdating =
    location.pathname === FIRMWARE_URL &&
    document.querySelector('[class*="type-body-lg mt-5"]')?.textContent?.includes("firmware")

  // Fetch brands for the dropdown
  useEffect(() => {
    const fetchBrands = async () => {
      try {
        const response = await axios.get("/api/equalizer/headphone-brands/")
        setBrands(response.data)
      } catch (err) {
        console.error("Error fetching headphone brands:", err)
      }
    }
    fetchBrands()
  }, [])

  // Fetch headphones when brand changes
  useEffect(() => {
    const fetchHeadphones = async () => {
      // Only fetch if we have a brand selected
      if (!selectedBrandId) {
        setHeadphones([])
        return
      }

      try {
        const response = await axios.get(`/api/equalizer/headphones/`)
        // Filter headphones by selected brand and sort by model name
        const filteredHeadphones = response.data
          .filter((h) => h.brand.id === selectedBrandId)
          .sort((a, b) => a.model.localeCompare(b.model))
        setHeadphones(filteredHeadphones)
      } catch (err) {
        console.error("Error fetching headphones:", err)
      }
    }
    fetchHeadphones()
  }, [selectedBrandId])

  // Fetch presets when modal opens or headphone selection changes
  useEffect(() => {
    const fetchPresets = async () => {
      setIsLoading(true)
      setHasMore(true)
      try {
        const params: { brand?: string; model?: string; user_id?: string } = {}
        if (selectedHeadphone) {
          params.model = selectedHeadphone.model
          params.brand = selectedHeadphone.brand.id.toString()
        } else if (selectedBrandId) {
          params.brand = selectedBrandId.toString()
        }
        if (selectedCreatorId) {
          params.user_id = selectedCreatorId.toString()
        }
        const response = await axios.get("/api/equalizer/public-presets/", { params })
        setPresets(response.data.results)
        setNextPage(response.data.next)
        setHasMore(!!response.data.next)
        console.log(`Initial load: ${response.data.results.length} presets loaded`)
      } catch (err) {
        console.error("Error fetching presets:", err)
      } finally {
        setIsLoading(false)
      }
    }

    if (isOpen) {
      fetchPresets()
    }
  }, [isOpen, selectedHeadphone, selectedBrandId, selectedCreatorId])

  const loadMorePresets = useCallback(async () => {
    if (!nextPage || isLoading || !hasMore) return

    setIsLoading(true)
    try {
      const response = await axios.get(nextPage)
      setPresets((prev) => [...prev, ...response.data.results])
      setNextPage(response.data.next)
      setHasMore(!!response.data.next)
      console.log(
        `Loaded more: ${response.data.results.length} presets. Total: ${response.data.results.length + presets.length}`
      )
    } catch (err) {
      console.error("Error loading more presets:", err)
    } finally {
      setIsLoading(false)
    }
  }, [nextPage, isLoading, hasMore, presets.length])

  const handleScroll = useCallback(() => {
    if (!scrollContainerRef.current) return

    const { scrollTop, scrollHeight, clientHeight } = scrollContainerRef.current
    // Load more when user has scrolled to within 100px of the bottom
    if (scrollHeight - scrollTop - clientHeight < 100) {
      loadMorePresets()
    }
  }, [loadMorePresets])

  // Add scroll event listener
  useEffect(() => {
    const scrollContainer = scrollContainerRef.current
    if (scrollContainer) {
      scrollContainer.addEventListener("scroll", handleScroll)
      return () => scrollContainer.removeEventListener("scroll", handleScroll)
    }
  }, [handleScroll])

  const handleListen = (preset) => {
    onPresetSelected(preset)
    // Don't close the modal when listening
  }

  const handleSave = async (preset) => {
    setIsSaving(true)
    try {
      console.log("Original preset headphone:", preset.headphone)

      // Create a new preset using the createPreset function from parent
      const newPreset = await createPreset({
        name: `${preset.name} (from ${preset.creator_name})`,
        mode: preset.mode,
        config: preset.config,
        headphone: preset.headphone || null,
      })

      console.log("Created new preset:", newPreset)

      if (newPreset) {
        toast.success("Preset saved successfully")
        // Load the saved preset to the device
        onPresetSelected(newPreset)
        onClose()
      } else {
        throw new Error("Failed to create preset")
      }
    } catch (err) {
      console.error("Error saving preset:", err)
      toast.error("Failed to save preset")
    } finally {
      setIsSaving(false)
    }
  }

  const handleShare = (preset, currentPage) => {
    // Generate shareable URL with preset ID and page number
    const shareUrl = `${window.location.origin}${window.location.pathname}?preset=${preset.id}&page=${currentPage}`
    navigator.clipboard.writeText(shareUrl)
    toast.success("Share link copied to clipboard")
  }

  return (
    <Modal isOpen={isOpen} onClose={onClose}>
      <ModalContent className="h-[72vh] max-w-[72vw] bg-dark/40 backdrop-blur-sm">
        {(onClose) => (
          <>
            <ModalBody className="flex h-full flex-col overflow-hidden">
              <h2 className="type-body-xl font-bold text-white">Browse Community Presets</h2>
              <div className="mt-3 flex flex-1 flex-col space-y-4 overflow-hidden">
                <div className="grid grid-cols-2 gap-x-4">
                  <div>
                    <div className="type-body-xs mb-2 text-silver">Filter by Brand</div>
                    <Select
                      selectedKeys={
                        selectedBrandId ? new Set([selectedBrandId.toString()]) : new Set(["none"])
                      }
                      onChange={(e) => {
                        const brandId = e.target.value ? parseInt(e.target.value) : null
                        setSelectedBrandId(brandId)
                        // Clear headphone selection when brand changes
                        setSelectedHeadphone(null)
                      }}
                      aria-label="Filter by Brand"
                      classNames={{
                        trigger:
                          "border border-[#383C4A] data-[hover=true]:border-[#626675] data-[hover=true]:bg-[#272A2F] transition-colors rounded-plus pl-4 bg-[#232528] min-h-7 h-7 shadow-auto-save-dropdown",
                        value:
                          "text-ghost group-data-[has-value=true]:text-white text-xs font-bulo font-bold",
                        popoverContent:
                          "bg-[#212121] rounded-plus border border-[#2E3037] p-0 shadow-outside text-ghost",
                        selectorIcon: "w-2.5 h-1.5",
                      }}
                      listboxProps={{
                        itemClasses: {
                          base: [
                            "!rounded-plus pl-3 py-1.5",
                            "data-[hover=true]:text-ghost",
                            "data-[hover=true]:bg-mine-shaft",
                            "data-[selectable=true]:focus:text-ghost",
                            "data-[selectable=true]:focus:bg-mine-shaft",
                          ],
                          title: "font-bulo text-xs font-bold",
                        },
                      }}
                      popoverProps={{
                        placement: "bottom-start",
                        motionProps: {
                          variants: {
                            initial: {
                              opacity: 0,
                              transform: "scaleY(0.9)",
                            },
                            enter: {
                              opacity: 1,
                              transform: "scaleY(1)",
                              transition: {
                                type: "spring",
                                bounce: 0,
                                duration: 0.3,
                              },
                            },
                            exit: {
                              opacity: 0,
                              transform: "scaleY(0.96)",
                              transition: {
                                type: "easeOut",
                                bounce: 0,
                                duration: 0.15,
                              },
                            },
                          },
                        },
                      }}
                      className="w-full"
                    >
                      <SelectItem key="none" value={null}>
                        Any Brand
                      </SelectItem>
                      {brands.map((brand) => (
                        <SelectItem key={brand.id} value={brand.id}>
                          {brand.name}
                        </SelectItem>
                      ))}
                    </Select>
                  </div>
                  <div>
                    <div className="type-body-xs mb-2 text-silver">Filter by Model</div>
                    <Select
                      selectedKeys={
                        selectedHeadphone ? new Set([selectedHeadphone.id.toString()]) : new Set()
                      }
                      onChange={(e) => {
                        const headphoneId = e.target.value ? parseInt(e.target.value) : null
                        const headphone = headphones.find((h) => h.id === headphoneId) || null
                        setSelectedHeadphone(headphone)
                      }}
                      isDisabled={!selectedBrandId}
                      aria-label="Filter by Model"
                      classNames={{
                        trigger:
                          "border border-[#383C4A] data-[hover=true]:border-[#626675] data-[hover=true]:bg-[#272A2F] transition-colors rounded-plus pl-4 bg-[#232528] min-h-7 h-7 shadow-auto-save-dropdown",
                        value:
                          "text-ghost group-data-[has-value=true]:text-white text-xs font-bulo font-bold",
                        popoverContent:
                          "bg-[#212121] rounded-plus border border-[#2E3037] p-0 shadow-outside text-ghost",
                        selectorIcon: "w-2.5 h-1.5",
                      }}
                      listboxProps={{
                        itemClasses: {
                          base: [
                            "!rounded-plus pl-3 py-1.5",
                            "data-[hover=true]:text-ghost",
                            "data-[hover=true]:bg-mine-shaft",
                            "data-[selectable=true]:focus:text-ghost",
                            "data-[selectable=true]:focus:bg-mine-shaft",
                          ],
                          title: "font-bulo text-xs font-bold",
                        },
                      }}
                      popoverProps={{
                        placement: "bottom-start",
                        motionProps: {
                          variants: {
                            initial: {
                              opacity: 0,
                              transform: "scaleY(0.9)",
                            },
                            enter: {
                              opacity: 1,
                              transform: "scaleY(1)",
                              transition: {
                                type: "spring",
                                bounce: 0,
                                duration: 0.3,
                              },
                            },
                            exit: {
                              opacity: 0,
                              transform: "scaleY(0.96)",
                              transition: {
                                type: "easeOut",
                                bounce: 0,
                                duration: 0.15,
                              },
                            },
                          },
                        },
                      }}
                      className="w-full"
                    >
                      <SelectItem key="none" value={null}>
                        Any Model
                      </SelectItem>
                      {headphones.map((headphone) => (
                        <SelectItem key={headphone.id} value={headphone.id}>
                          {headphone.model}
                        </SelectItem>
                      ))}
                    </Select>
                  </div>
                </div>

                {!isAuthenticated() && (
                  <div className="flex items-center justify-between rounded-md border border-[#626675]/30 bg-[#272A2F]/40 px-4 py-2">
                    <div className="flex w-full items-center justify-center gap-2">
                      <span className="text-lg">⚠️</span>
                      <span className="type-body-base text-[#858484]">
                        <span className="font-bold">You are currently logged out.</span> Please log
                        in to Save Cloud Presets.
                      </span>
                    </div>
                  </div>
                )}

                {firmwareStatus === FIRMWARE_OUTDATED && !isFirmwareUpdating && (
                  <NavLink
                    to={FIRMWARE_URL}
                    className="type-body-base flex cursor-pointer items-center justify-center rounded-md border border-tundora py-2.5 pl-4 pr-6 font-bold transition-colors hover:border-silver"
                  >
                    <ToolIcon className="mr-4 w-5" />
                    FW
                    <span className="type-body-base ml-3 inline-block font-bold italic text-orange-400">
                      Please update to latest firmware to ensure compatibility with all presets! (
                      {deviceFirmwareVersion} &raquo; {latestFirmware.version})
                    </span>
                  </NavLink>
                )}

                {selectedCreatorId && (
                  <div className="flex items-center justify-between rounded-md border border-[#FFB800]/30 bg-[#FFB800]/5 px-4 py-2">
                    <span className="type-body-xs text-[#FFB800]">
                      Showing presets by {presets[0]?.creator_name}
                    </span>
                    <button
                      onClick={() => setSelectedCreatorId(null)}
                      className="type-body-xs flex items-center gap-x-1 text-[#FFB800] hover:text-white"
                    >
                      <span>×</span>
                      <span>Clear Filter</span>
                    </button>
                  </div>
                )}

                <div
                  ref={scrollContainerRef}
                  className="flex-1 overflow-y-auto [&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-thumb]:bg-jaguar hover:[&::-webkit-scrollbar-thumb]:bg-silver [&::-webkit-scrollbar-track]:bg-dark-jaguar/40 [&::-webkit-scrollbar]:w-2"
                >
                  {isLoading && presets.length === 0 ? (
                    <div className="flex h-full items-center justify-center">
                      <span className="flex items-center gap-2">
                        <LoadingSpinner size="small" />
                        <span className="text-xs text-gray-500">LOADING PRESETS</span>
                      </span>
                    </div>
                  ) : presets.length === 0 ? (
                    <p className="text-sm font-normal text-silver">
                      No presets found. Have one? Please share it!
                    </p>
                  ) : (
                    <>
                      <div className="grid grid-cols-1 gap-4 p-1 sm:grid-cols-2 lg:grid-cols-3">
                        {presets.map((preset) => (
                          <div
                            key={preset.id}
                            className="group flex flex-col rounded-plus border border-[#3D3D3D] bg-[#2B2B2B]/40 p-4 transition-colors duration-200 hover:border-[#4A4A4A] hover:bg-mine-shaft/40"
                          >
                            <div className="flex items-center justify-between">
                              <h2 className="type-body-sm font-medium text-white">{preset.name}</h2>
                            </div>
                            <h3 className="type-body-xs mt-1 font-medium text-silver">
                              {preset.headphone ? (
                                <>
                                  {preset.headphone.brand.name} {preset.headphone.model}
                                </>
                              ) : (
                                "No headphone specified"
                              )}
                            </h3>
                            <div className="mt-2 flex items-center gap-x-2">
                              <button
                                onClick={() => {
                                  setSelectedCreatorId(preset.creator_id)
                                }}
                                className={`type-body-xs ${
                                  preset.is_official
                                    ? "rounded-full bg-[#FFB800]/20 px-2 py-0.5 font-medium text-[#FFB800]"
                                    : "text-[#858484] hover:text-white"
                                } transition-colors`}
                              >
                                by {preset.creator_name}
                              </button>
                            </div>
                            <div className="mt-4 flex items-center gap-x-2">
                              <button
                                className="type-body-xs flex items-center gap-x-1.5 rounded-plus border border-jaguar bg-dark-jaguar/40 px-3 py-1.5 font-medium text-silver transition-colors hover:border-silver hover:text-white disabled:opacity-50"
                                onClick={() => handleListen(preset)}
                              >
                                <HeadphoneIcon className="h-3 w-3" />
                                Listen
                              </button>
                              <button
                                className="type-body-xs flex items-center gap-x-1.5 rounded-plus border border-jaguar bg-dark-jaguar/40 px-3 py-1.5 font-medium text-silver transition-colors hover:border-silver hover:text-white disabled:opacity-50"
                                onClick={() => handleSave(preset)}
                                disabled={isSaving || !isAuthenticated()}
                              >
                                <CloudIcon className="h-3 w-3" />
                                {isSaving ? "Saving..." : "Save to Presets"}
                              </button>
                              <button
                                className="type-body-xs flex items-center gap-x-1.5 rounded-plus border border-jaguar bg-dark-jaguar/40 px-3 py-1.5 font-medium text-silver transition-colors hover:border-silver hover:text-white"
                                onClick={() =>
                                  handleShare(preset, Math.floor(presets.indexOf(preset) / 50) + 1)
                                }
                              >
                                <ShareIcon className="h-3 w-3" />
                                Share
                              </button>
                            </div>
                          </div>
                        ))}
                      </div>
                      {isLoading && (
                        <div className="flex justify-center py-4">
                          <span className="flex items-center gap-2">
                            <LoadingSpinner size="small" />
                            <span className="text-xs text-gray-500">LOADING MORE...</span>
                          </span>
                        </div>
                      )}
                    </>
                  )}
                </div>
              </div>
            </ModalBody>
            <ModalFooter className="gap-x-0">
              <Button
                className="-mr-5"
                theme="none"
                size="sm"
                outline={false}
                solid={true}
                full={false}
                onClick={onClose}
              >
                Close
              </Button>
            </ModalFooter>
          </>
        )}
      </ModalContent>
    </Modal>
  )
}

export default SharedPresetsModal
